"use server";

import {
  TGenericResponse,
  TResponseServerAction,
  TInterview,
  TAppointmentAction,
  TConference,
} from "./types";
import { api, ErrorResponseData, type AxiosError } from "@/lib/axios";
import { getSessionApiKeySync } from "@/lib/cookies";
import { revalidatePath } from "next/cache";

export const getInterviews = async (
  status: string,
): Promise<TResponseServerAction<TInterview[]>> => {
  if (!getSessionApiKeySync()) {
    return {
      status: "error",
      message: "Not authenticated",
    };
  }
  try {
    const response = await api.get<TGenericResponse<TInterview[]>>(
      "/Interviews",
      {
        params: {
          status,
        },
      },
    );

    const raw = response.data?.data;
    const metaData = Array.isArray(raw) ? raw : [];

    return {
      status: "success",
      message: "Interviews fetched successfully",
      metaData,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;
    console.log({ axiosError });

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Interviews fetch failed"
          : "Interviews fetch failed",
    };
  }
};

export const getInterviewConference = async (
  interviewId: string,
): Promise<TResponseServerAction<TConference>> => {
  if (!getSessionApiKeySync()) {
    return {
      status: "error",
      message: "Not authenticated",
    };
  }
  try {
    const response = await api.get<TGenericResponse<TConference>>(
      `/Interviews/${interviewId}/conference`,
      {},
    );

    return {
      status: "success",
      message: "Conference fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Conference fetch failed"
          : "Conference fetch failed",
    };
  }
};

export const getInterviewById = async (
  interviewId: string,
): Promise<TResponseServerAction<TInterview>> => {
  if (!getSessionApiKeySync()) {
    return {
      status: "error",
      message: "Not authenticated",
    };
  }
  try {
    const response = await api.get<TGenericResponse<TInterview>>(
      `/Interviews/${interviewId}`,
      {},
    );

    return {
      status: "success",
      message: "Interview fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.message || "Interview fetch failed"
          : "Interview fetch failed",
    };
  }
};

export const requestInterview = async (
  participantId: string,
  payload: {
    date: string;
    time: string;
    timeZone: string;
  },
): Promise<TResponseServerAction<null>> => {
  if (!getSessionApiKeySync()) {
    return {
      status: "error",
      message: "Not authenticated",
    };
  }
  try {
    const response = await api.post<TGenericResponse<null>>(
      `/Interviews/${participantId}/Request`,
      payload,
    );

    revalidatePath("/schedules");
    return {
      status: "success",
      message: "Interview requested successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Interview request failed"
          : "Interview request failed",
    };
  }
};

export const updateInterviewStatus = async (
  interviewId: string,
  payload: {
    appointmentAction: TAppointmentAction;
    reason: string;
  },
): Promise<TResponseServerAction<null>> => {
  if (!getSessionApiKeySync()) {
    return {
      status: "error",
      message: "Not authenticated",
    };
  }
  try {
    const response = await api.put<TGenericResponse<null>>(
      `/Interviews/${interviewId}/Status`,
      payload,
    );

    revalidatePath("/schedules");

    return {
      status: "success",
      message: "Interview status updated successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Interview status update failed"
          : "Interview status update failed",
    };
  }
};
